import React, { useState } from 'react'
import { GetStaticProps, NextPage } from 'next'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'

import { Layout } from '@components/layout'
import { HeaderSection } from '@components/header-section'
import SEO from '@components/shared/seo'

import { MenusApi, Menu } from '@services/api/menus'
import { ArticlesApi } from '@services/api/articles'
import { Article } from '@helpers/article-response'
import { AdsApi } from '@services/api/ads'
import { AdsIndexDto } from '@services/api/ads/types'
import NewscastsService, { Newscast } from '@services/api/newscasts'
import { SponsorBanner } from '@components/sponsor-banner'
import { AuthorsApi, Author } from '@services/api/authors'
import { get } from 'http'
import { useWindowDimensions } from '@hooks/use-window-dimensions'
import { SystemsSoldSection } from '@components/home/systems-sold-section'
import { BlockCall } from 'types/block-call'
import restFetch from '@services/rest-fetch'
import { ArticlesTabs } from '@components/articles-tabs'

interface SpecialistPageProps {
  main: Menu[]
  projects: Array<BlockCall>
  footer1: Menu[]
  footer2: Menu[]
  specialistArticles: Article[]
  specialists: Author[]
  ads: AdsIndexDto[]
  newscasts?: Newscast[]
}

const SpecialistsPage: NextPage<SpecialistPageProps> = ({
  main,
  projects,
  footer1,
  footer2,
  specialistArticles,
  specialists,
  ads,
  newscasts = []
}) => {
  const { width } = useWindowDimensions()
  const [selectedCategory, setSelectedCategory] = useState<
    'all' | 'leadership' | 'marketing' | 'solar'
  >('all')

  // Função helper para formatar datas de forma consistente (evita erro de hidratação)
  const formatDate = (dateString: string): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  const getSpecialistCompanyName = (companyName?: string): string[] => {
    if (!companyName) return []
    // se tiver | no companyName, separar e retornar em array todos os valores separadamente
    const parts = companyName.split('|')

    return parts.length >= 1 ? parts : []
  }
  // Filtrar artigos baseado na categoria selecionada
  const featuredArticles = specialistArticles.slice(0, 30)
  return (
    <Layout
      mainMenus={main}
      footerMenus1={footer1}
      footerMenus2={footer2}
      fullContent
      title="Portal Solar - Especialistas"
      logoHeading={true}
      logoUrl="/"
      ad={ads?.find(ad => ad.code === 'ad_sponsor_main_top')}
    >
      <Head>
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Portal Solar - Especialistas" />
        <meta
          property="og:description"
          content="Conheça os especialistas em energia solar do Portal Solar e acesse artigos exclusivos sobre o setor."
        />
      </Head>

      <SEO
        title="Portal Solar - Especialistas"
        description="Conheça os especialistas em energia solar do Portal Solar e acesse artigos exclusivos sobre o setor."
        canonicalUrl={`${
          process.env.NEXT_PUBLIC_URL_BASE || 'https://www.portalsolar.com.br'
        }/especialistas`}
        type="website"
        shouldIndexPage={true}
      />

      {/* Header da página */}
      <HeaderSection className="py-8 mx-5">
        <h1 className="text-4xl font-bold">Área dos Especialistas</h1>
      </HeaderSection>

      {/* Seção de artigos em destaque */}
      <section className="container-fluid mx-5 px-4 py-8">
        {/* Grid principal com artigo destacado */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8 relative">
          {/* Sidebar esquerdo com artigos menores */}
          <div className="lg:col-span-3 grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 relative">
            <div className="space-y-4">
              {featuredArticles.slice(1, 6).map(article => (
                <a
                  key={article.id}
                  href={article.path}
                  className={`cursor-pointer font-noto w-full h-auto flex flex-col mb-2 group`}
                >
                  <div className="bg-white rounded-lg overflow-hide">
                    <div className="relative h-56">
                      <Image
                        src={
                          article.thumbnailImage?.path ||
                          article.thumbnail_image?.path ||
                          '/assets/default-article.jpg'
                        }
                        alt={
                          article.thumbnailImage?.alt ||
                          article.thumbnail_image?.alt ||
                          article.title
                        }
                        fill
                        className="object-cover rounded-lg group-hover:scale-[101%] transition-transform duration-300"
                      />
                    </div>
                    <div className="py-2 relative h-auto">
                      <span className="bg-[#F1C786]  px-3 py-1 rounded text-sm font-medium mr-2 mb-2">
                        {article.editorial?.name || 'NOTÍCIA'}
                      </span>
                      <h3 className="font-bold text-lg mb-2 h-14 overflow-hidden  line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-gray-200 text-sm line-clamp-4 mb-4">
                        {article.short_content ||
                          'Conteúdo especializado sobre energia solar.'}
                      </p>

                      <p className="text-xs text-gray-500 flex justify-between w-full absolute bottom-1">
                        <span>
                          {formatDate(
                            article.published_at || article.publishedAt
                          )}
                        </span>
                        <span className="pe-4">
                          {article.time_to_read || article.timeToRead} min de
                          leitura
                        </span>
                      </p>
                    </div>
                  </div>
                </a>
              ))}
            </div>
            {/* Artigo principal grande */}
            <div className="lg:col-span-2">
              {featuredArticles[0] && (
                <a
                  key={featuredArticles[0].id}
                  href={featuredArticles[0].path}
                  className={`cursor-pointer font-noto w-full h-auto flex flex-col mb-2 group`}
                >
                  <div className="bg-white rounded-lg overflow-hide">
                    <div className="relative h-96">
                      <Image
                        src={
                          featuredArticles[0].thumbnailImage?.path ||
                          featuredArticles[0].thumbnail_image?.path ||
                          '/assets/default-featuredArticles[0].jpg'
                        }
                        alt={
                          featuredArticles[0].thumbnailImage?.alt ||
                          featuredArticles[0].thumbnail_image?.alt ||
                          featuredArticles[0].title
                        }
                        fill
                        className="object-cover rounded-lg group-hover:scale-[101%] transition-transform duration-300"
                      />
                    </div>
                    <div className="py-2 relative h-auto">
                      <span className="bg-[#F1C786]  px-3 py-1 rounded text-sm font-medium mr-2 mb-2">
                        {featuredArticles[0].editorial?.name || 'NOTÍCIA'}
                      </span>
                      <h3 className="font-bold text-lg mb-2  overflow-hidden  line-clamp-1">
                        {featuredArticles[0].title}
                      </h3>

                      <p className="text-gray-200 text-sm line-clamp-4 mb-4">
                        {featuredArticles[0].short_content ||
                          'Conteúdo especializado sobre energia solar.'}
                      </p>

                      <p className="text-xs text-gray-500 flex justify-between w-full absolute bottom-1">
                        <span>
                          {formatDate(
                            featuredArticles[0].publishedAt ||
                              featuredArticles[0].published_at
                          )}
                        </span>
                        <span className="pe-2">
                          {featuredArticles[0].time_to_read} min de leitura
                        </span>
                      </p>
                    </div>
                  </div>
                </a>
              )}
              {featuredArticles.length > 10 &&
                featuredArticles.slice(10, 17).map(article => (
                  <Link key={article.id} href={article.path}>
                    <div className="flex gap-1 rounded-lg cursor-pointer mb-4 group">
                      <div className="relative w-2/5 h-48 flex-shrink-0">
                        <Image
                          src={
                            article.thumbnailImage?.path ||
                            article.thumbnail_image?.path ||
                            '/assets/default-article.jpg'
                          }
                          alt={article.title}
                          fill
                          className="object-cover rounded group-hover:scale-[103%] transition-transform duration-300"
                        />
                      </div>
                      <div className="flex-1 px-2 pt-2 pb-0 relative ">
                        <h3 className="text-xl font-semibold leading-tight line-clamp-1">
                          {article.title}
                        </h3>
                        <p className="bg-[#F1C786]  px-3 py-0.5 rounded text-xs font-medium mr-2 mb-1 w-fit">
                          {article.editorial?.name || 'Portal Solar'}
                        </p>
                        <p className="text-sm text-gray-500 line-clamp-4">
                          {article.shortContent ||
                            'Descubra insights valiosos sobre energia solar e sustentabilidade.'}
                        </p>
                        <p className="text-xs text-gray-400 flex justify-between items-end -mb-1 w-full absolute bottom-0">
                          <span>
                            {formatDate(
                              article.publishedAt || article.published_at
                            )}
                          </span>
                          <span className="pe-4">
                            {article.timeToRead || article.time_to_read} min de
                            leitura
                          </span>
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
            <div className="col-span-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 h-fit">
              <div className="flex justify-between items-center mb-8 col-span-1 md:col-span-2 lg:col-span-4 ">
                <h2 className="text-3xl font-bold">
                  Conheça nossos especialistas
                </h2>
                <Link
                  href="/noticias/articulistas"
                  className="hover:bg-gray-100 rounded-full border px-2 py-1 text-gray-400"
                >
                  Ver mais
                </Link>
              </div>
              {specialists.map(specialist => (
                <Link
                  key={specialist.id}
                  href={specialist.path || `/especialistas/${specialist.slug}`}
                  className="h-80 mb-4"
                >
                  <div className="bg-white rounded-lg overflow-hidden cursor-pointer">
                    <div className="relative h-48">
                      <Image
                        src={
                          specialist.avatar || '/assets/default-specialist.jpg'
                        }
                        alt={specialist.name || 'Especialista'}
                        fill
                        className="object-cover rounded-lg"
                      />
                    </div>

                    <div className="pb-4 pt-2 h-32 ">
                      <div className="h-10 mb-1 flex flex-wrap">
                        {getSpecialistCompanyName(specialist.companyName)?.map(
                          (company, index) => (
                            <p
                              key={index}
                              className="bg-[#F1C786] px-1.5 py-0 rounded text-[9px]  mr-1 mb-1 h-fit w-fit whitespace-nowrap"
                            >
                              {company}
                            </p>
                          )
                        )}
                      </div>
                      <h3 className="font-bold text-lg mb-1 line-clamp-1">
                        {specialist.name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {specialist.biography}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="col-span-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 h-fit">
              <section
                id="systems-sold"
                className="col-span-1 md:col-span-2 lg:col-span-4"
              >
                <SystemsSoldSection data={projects} />
              </section>
              <section
                id="systems-sold"
                className="col-span-1 md:col-span-2 lg:col-span-4"
              >
                <ArticlesTabs articlesPerPage={6} showGuideTab={true} />
              </section>
            </div>
          </div>

          {/* Sidebar direito com artigos menores */}
          <div className="space-y-4 relative flex flex-col">
            <div>
              {featuredArticles.length > 3 &&
                featuredArticles.slice(6, 10).map(article => (
                  <Link key={article.id} href={article.path}>
                    <div className="flex gap-1 rounded-lg cursor-pointer mb-4 group">
                      <div className="flex-1 px-2 pt-2 pb-0 relative ">
                        <h3 className="text-sm font-semibold leading-tight line-clamp-1">
                          {article.title}
                        </h3>
                        <p className="bg-[#F1C786]  px-3 py-0.5 rounded text-xs font-medium mr-2 mb-1 w-fit">
                          {article.editorial?.name || 'Portal Solar'}
                        </p>
                        <p className="text-xs text-gray-500 line-clamp-2">
                          {article.shortContent ||
                            'Descubra insights valiosos sobre energia solar e sustentabilidade.'}
                        </p>
                        <p className="text-xs text-gray-400 flex justify-between items-end -mb-1 w-full absolute bottom-0">
                          <span>
                            {formatDate(
                              article.publishedAt || article.published_at
                            )}
                          </span>
                          <span className="pe-4">
                            {article.timeToRead || article.time_to_read} min de
                            leitura
                          </span>
                        </p>
                      </div>
                      <div className="relative w-24 h-24 flex-shrink-0">
                        <Image
                          src={
                            article.thumbnailImage?.path ||
                            article.thumbnail_image?.path ||
                            '/assets/default-article.jpg'
                          }
                          alt={article.title}
                          fill
                          className="object-cover rounded group-hover:scale-[103%] transition-transform duration-300"
                        />
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
            <div className="relative flex flex-col justify-around flex-1">
              {/* SEÇÃO PROPAGANDA AZUL */}
              {ads?.find(ad => ad.code === 'ad_direita_especialistas') && (
                <SponsorBanner
                  className="mt-10"
                  config={ads?.find(
                    ad => ad.code === 'ad_direita_especialistas'
                  )}
                />
              )}
              {ads?.find(ad => ad.code === 'ad_direita_especialistas2') && (
                <SponsorBanner
                  className="mt-10"
                  config={ads?.find(
                    ad => ad.code === 'ad_direita_especialistas'
                  )}
                />
              )}
              {ads?.find(ad => ad.code === 'ad_direita_especialistas3') && (
                <SponsorBanner
                  className="mt-10"
                  config={ads?.find(
                    ad => ad.code === 'ad_direita_especialistas'
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  try {
    const [
      menus,
      {
        data: { articles: specialistArticles }
      },
      {
        data: { articles: latestArticles }
      },
      {
        data: { authors: specialists }
      },
      newscastsResponse
    ] = await Promise.all([
      MenusApi.all(),
      ArticlesApi.specialistsHighlights(),
      ArticlesApi.latest(30), // Busca 30 últimos artigos
      AuthorsApi.index({ per: 8 }), // Busca 8 especialistas
      NewscastsService.index({ size: 999 })
    ])
    const { blockCalls: projects } = await restFetch<{
      blockCalls: BlockCall[]
    }>('/block_calls?block_type=home_solar_projects')
    const {
      data: { ads }
    } = await AdsApi.index({
      codes: [
        'ad_topo_especialistas',
        'ad_direita_especialistas',
        'ad_direita_especialistas2',
        'ad_direita_especialistas3',
        'ad_fundo_especialistas'
      ]
    })

    // Função para normalizar artigos (converter snake_case para camelCase)
    const normalizeArticle = (article: any): Article => ({
      ...article,
      publishedAt: article.publishedAt || article.published_at,
      shortContent: article.shortContent || article.short_content,
      timeToRead: article.timeToRead || article.time_to_read,
      thumbnailImage: article.thumbnailImage || article.thumbnail_image,
      // Manter compatibilidade com snake_case
      published_at: article.published_at || article.publishedAt,
      short_content: article.short_content || article.shortContent,
      time_to_read: article.time_to_read || article.timeToRead,
      thumbnail_image: article.thumbnail_image || article.thumbnailImage
    })

    // Normalizar todos os artigos
    const normalizedSpecialistArticles =
      specialistArticles.map(normalizeArticle)
    const normalizedLatestArticles = latestArticles.map(normalizeArticle)

    // Combinar artigos dos especialistas com os últimos artigos para garantir variedade
    const allArticles = [
      ...normalizedSpecialistArticles,
      ...normalizedLatestArticles.filter(
        article =>
          !normalizedSpecialistArticles.some(
            specArticle => specArticle.id === article.id
          )
      )
    ]

    return {
      props: {
        main: menus.main,
        footer1: menus.footer1,
        footer2: menus.footer2,
        specialistArticles: allArticles || [],
        specialists: specialists || [],
        ads: ads || [],
        newscasts: newscastsResponse.data || [],
        projects
      },
      revalidate: 60 * 30 // 30 minutes
    }
  } catch (error) {
    console.error('Error fetching specialists data:', error)
    return {
      props: {
        main: [],
        projects: [],
        footer1: [],
        footer2: [],
        specialistArticles: [],
        specialists: [],
        ads: [],
        newscasts: []
      }
    }
  }
}

export default SpecialistsPage
